import { Add } from '@mui/icons-material';
import { Chip, IconButton, Stack, useTheme } from '@mui/material';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { camelCase } from 'lodash-es';
import { type MouseEvent, useState } from 'react';
import { FiltersDrawer } from '#components/FiltersDrawer/index.tsx';
import { Link } from '#components/Link/index.tsx';
import { buildDiscoverFieldsUrl } from '#helpers/buildDiscoverFieldsUrl.ts';
import { useStore } from '#store/useStore.ts';

export type ShortcutsChipStackProps = {
  invertColor?: boolean;
};

export const ShortcutsChipStack = ({ invertColor }: ShortcutsChipStackProps) => {
  const theme = useTheme();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const addShortcut = useStore(store => store.addShortcut);
  const removeShortcut = useStore(store => store.removeShortcut);
  const shortcuts = useStore(store => store.shortcuts);

  return (
    <>
      <Stack
        direction="row"
        spacing={{ md: 2, sm: 1, xs: 1 }}
        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        useFlexGap
      >
        <>
          {shortcuts.map(chip => (
            <Chip
              color="default"
              component={Link}
              href={chip.href}
              key={camelCase(chip.label)}
              label={chip.label}
              onDelete={event => {
                // Casting as despite setting component to anchor tag, the event
                // type is still a HTMLDivElement, which doesn't have .href attr.
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                const castEvent = event as unknown as MouseEvent<HTMLAnchorElement>;
                castEvent.preventDefault();
                removeShortcut(chip.label);
              }}
              sx={{
                '&:hover': {
                  color: invertColor ? theme.mainTextSecondary : undefined,
                  cursor: 'pointer',
                },
                '.MuiChip-deleteIcon': {
                  '&:hover': {
                    color: invertColor ? theme.mainTextSecondary : undefined,
                  },
                  color: invertColor ? theme.supportingTextSecondary : undefined,
                },
                color: invertColor ? theme.supportingTextSecondary : undefined,
              }}
              variant="outlined"
            />
          ))}
          <IconButton
            aria-label="Add a shortcut"
            onClick={() => {
              setFiltersOpen(true);
            }}
            sx={{
              color: theme.mainTextSecondary,
            }}
          >
            <Add />
          </IconButton>
        </>
      </Stack>
      <FiltersDrawer
        addToShortcuts
        open={filtersOpen}
        setFiltersParam={(value = {}, pathnameOverride = 'movie') => {
          const { shortcutName, ...rest } = value;

          if (shortcutName) {
            addShortcut({
              href: buildDiscoverFieldsUrl(rest, `discover/${pathnameOverride}`, new URLSearchParams()),
              label: shortcutName,
            });
          }
        }}
        setOpen={setFiltersOpen}
        title="Add shortcut"
        type={ScreenType.Movie}
      />
    </>
  );
};
